import { forwardRef, useEffect, useImperativeHandle } from "react"
import { useCreateAddress } from "../../hooks/People/useCreateAddress"
import { AddressEntity } from "../../Objects/Person/AddressEntity"
import { useTranslation } from "react-i18next"
import { DeliveryMapPicker } from "../DeliveryMapPicker/DeliveryMapPicker"
import InputField from "../InputField/InputField"
import { faCircleInfo, faTag } from "@fortawesome/free-solid-svg-icons"
import { AddressPreviewCard } from "./AddressPreviewCardProps"

interface AddressFormProps {
  addressEntity?: AddressEntity
  disable: boolean
}

export const AddressForm = forwardRef((props: AddressFormProps, ref) => {
  const { t } = useTranslation("address")

  const {
    city,
    street,
    state,
    neighBornHood,
    number,
    complement,
    cep,
    label,
    latitude,
    longitude,
    handleLabelChange,
    handleComplementChange,
    handleSubmitForm,
    handleSetInitialValue,
    handleApplyGoogleAddress,
  } = useCreateAddress()

  useEffect(() => {
    handleSetInitialValue(props.addressEntity)
  }, [props.addressEntity])

  useImperativeHandle(ref, () => ({
    validate: () => handleSubmitForm(),
  }))

  return (
    <div className="w-full flex justify-center">
      <div className="w-full max-w-2xl space-y-4">
        {!props.disable && (
          <div className="rounded-lg overflow-hidden border border-gray-200 p-4">
            <DeliveryMapPicker
              initialLatitude={props.addressEntity?.latitude}
              initialLongitude={props.addressEntity?.longitude}
              onSelect={({ addressComponents, latitude, longitude }) => {
                if (!addressComponents) {
                  return
                }

                handleApplyGoogleAddress({
                  addressComponents,
                  latitude,
                  longitude,
                })
              }}
            />
          </div>
        )}

        <InputField
          maxLength={45}
          type="text"
          placeholder={t("label_person_label_place_holder")}
          onInvalidMessage={t("label_person_label_onInvalidMessage")}
          icon={faTag}
          value={label}
          disable={props.disable}
          onChange={handleLabelChange}
          required
          descriptionKey="interactionDescriptions:components.address.addressForm.input.personPlaceHolder"
        />

        <InputField
          maxLength={120}
          type="text"
          placeholder={t("label_location_details_place_holder")}
          onInvalidMessage={t("label_location_details_onInvalidMessage")}
          icon={faCircleInfo}
          value={complement}
          disable={props.disable}
          onChange={handleComplementChange}
          descriptionKey="interactionDescriptions:components.address.addressForm.input.locationDetailsPlaceHolder"
        />

        <AddressPreviewCard
          street={street}
          number={number}
          neighborhood={neighBornHood}
          city={city}
          state={state}
          cep={cep}
          complement={complement}
          latitude={latitude}
          longitude={longitude}
        />
      </div>
    </div>
  )
})
